var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { useConfigContext } from '../context';
import { initAtsEmbedReceiver, isEmbedded, connectorIdFromUrl } from '../api/ats-embed';
import { vritiApiClient } from '../api/api-service';
import { writeAccessToken, clearTokens, writeUserDetails, removeUserDetails } from '@/utils/auth-token-store';
import { cancelScheduledRefresh } from '@/utils/auth-refresh';
// Module-level guard: once the client token has been exchanged this page-load,
// a re-mounted App (the dev router swaps LandingPage↔Dashboard when isLoggedIn
// flips) must NOT re-run the handshake — otherwise it loops. Persists across
// re-mounts within the same page load (no reload needed).
let bootstrapped = false;
/**
 * Exchange the client token for a genuine IAM token and bootstrap the session.
 *
 * The FE then behaves exactly like a normally logged-in user: the real IAM token
 * is accepted by BOTH AI Interview and Vriti, so no per-request X-ATS-Connector
 * is needed and every call (including the org profile) works.
 */
const exchangeAndBootstrap = (clientToken, connectorId, apiBaseUrl) => __awaiter(void 0, void 0, void 0, function* () {
    // The exchange endpoint lives on the AI Interview backend. It can be overridden
    // for local testing via REACT_APP_ATS_EXCHANGE_URL (defaults to apiBaseUrl).
    const base = (process.env.REACT_APP_ATS_EXCHANGE_URL || apiBaseUrl || '').replace(/\/$/, '');
    const res = yield fetch(`${base}/ats/exchange-token/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clientToken}`,
            'X-ATS-Connector': connectorId,
        },
    });
    const body = yield res.json().catch(() => ({}));
    if (!res.ok || !(body === null || body === void 0 ? void 0 : body.access_token)) {
        throw new Error((body === null || body === void 0 ? void 0 : body.message) || `exchange failed (${res.status})`);
    }
    // Store the REAL IAM token the way the app reads it (login stores it stringified).
    writeAccessToken(body.access_token);
    // Fetch the org profile so the dashboard has user-details (as the login flow does).
    try {
        const profile = yield vritiApiClient.get('get-organization-profile/');
        if (profile === null || profile === void 0 ? void 0 : profile.data)
            writeUserDetails(JSON.stringify(profile.data));
    }
    catch (_a) {
        /* non-fatal: the profile can still be fetched by the app later */
    }
    // Mark bootstrapped BEFORE notifying, so the App that re-mounts when isLoggedIn
    // flips skips the handshake (no loop). Then nudge useAuthStatus (same-window
    // setItem doesn't fire a 'storage' event).
    bootstrapped = true;
    window.dispatchEvent(new Event('authChange'));
});
/**
 * Installs the ATS embed postMessage receiver (see ats-embed.ts).
 *
 * The connector id can come from the widget config OR the iframe URL
 * (?ats_connector=...). The URL form lets a client activate the embed flow purely
 * by how they load the iframe — without it being baked into config — so standalone
 * and Vriti-embed flows (neither of which sets it) are completely unaffected.
 *
 * Returns `waitingForToken`: true only for an ATS embed (connector id present + in
 * an iframe) that has not yet received a token AND has none stored. Callers use it
 * to hold protected content until the parent seeds the token.
 */
export const useAtsEmbed = () => {
    const { config: { connectorId: configConnectorId, apiBaseUrl }, } = useConfigContext();
    const connectorId = configConnectorId || connectorIdFromUrl();
    const isAtsEmbed = !!connectorId && isEmbedded();
    // Already exchanged this page-load? Then don't re-handshake — the re-mounted
    // (logged-in) App just renders normally.
    const [waitingForToken, setWaitingForToken] = useState(isAtsEmbed && !bootstrapped);
    useEffect(() => {
        if (!isAtsEmbed || bootstrapped) {
            setWaitingForToken(false);
            return;
        }
        // SECURITY (multi-tenant): never render a session left over from a previous
        // recruiter/client. Clear any stored token on each fresh embed load so the
        // parent MUST post a valid token for THIS recruiter — no stale-session bleed.
        // Cancel any pending renewal timer too, so nothing armed for the previous
        // tenant can fire after this reset.
        cancelScheduledRefresh();
        clearTokens();
        removeUserDetails();
        const cleanup = initAtsEmbedReceiver({
            connectorId,
            onAuth: (clientToken, cid) => {
                exchangeAndBootstrap(clientToken, cid, apiBaseUrl)
                    .catch((e) => console.error('[ats-embed] token exchange failed:', e))
                    // On success bootstrapped is set + authChange fired (re-mounts as
                    // logged-in). Either way, stop gating this instance.
                    .finally(() => setWaitingForToken(false));
            },
        });
        // Failsafe: if the parent never posts a token, stop gating — but since we
        // cleared the token above, the app stays logged-OUT (no stale dashboard).
        const timer = setTimeout(() => setWaitingForToken(false), 8000);
        return () => {
            cleanup();
            clearTimeout(timer);
        };
    }, [isAtsEmbed, connectorId, apiBaseUrl]);
    return { waitingForToken };
};
